import { cloneDeep } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { NodeData, TreeDataSourceOptions } from '../..';
import { NodeConfiguration, StaticNode } from '../../dataSourceTypes';
import { Validation } from '../../validators';
import { GzNodeDirectorWebComponent } from '../../../uiElements/gzNodeDirector';

export abstract class TreeDataSource<Options extends TreeDataSourceOptions = TreeDataSourceOptions> {
  protected nodes = new Map<string | number, NodeData>();
  public options: Options;
  public nodes$ = new BehaviorSubject(new Map());
  private activeNodeId: string;
  private editNodeId: string;
  public loading$ = new BehaviorSubject(false);

  constructor(options: Options) {
    this.setupTree(options);
  }

  private setupTree(options: Options) {
    this.options = {
      cacheEnabled: true,
      ...options,
    };
  }

  /**
   * Method used to trigger the opening of a node.
   */
  abstract toggleNodeOpen(node: NodeData, isOpen?: boolean): Promise<void>;

  abstract loadPagedNodes(targetNode: NodeData, page: number): void;

  /**
   * Method used to drop a node to the tree.
   */
  abstract dropNode(droppedNode: NodeData, containerNode: NodeData, nodeHandler: Function): Promise<Validation>;

  abstract updateNodeWithNewPosition(droppedNode: NodeData, containerNodeId: string | number): void;

  /**
   * Method used to attach a node to a container node.
   */
  public async attachNodeToContainer(node: NodeConfiguration, containerNode: NodeData): Promise<boolean> {
    this.nodes.set(node.nodeId, { ...node, parentNodeId: containerNode.nodeId });

    if (!containerNode.isOpen) {
      await this.toggleNodeOpen(containerNode, true);
    }

    let updatedContainerNode = this.nodes.get(containerNode.nodeId)!;

    if (updatedContainerNode.childNodeIds?.includes(node.nodeId)) {
      return false;
    }

    updatedContainerNode.childNodeIds?.push(node.nodeId);
    this.emitNodeChanges();

    return true;
  }

  /**
   * Sets individual node.
   */
  public setNode(node: NodeData) {
    this.nodes.set(node.nodeId, node);
    this.emitNodeChanges();
  }

  /**
   * Sets node and it's children to the node state.
   */
  public setNodes(currentNode: NodeData, childNodes: NodeData[]) {
    const existingNodeData = this.nodes.get(currentNode.nodeId) || {};

    this.nodes.set(currentNode.nodeId, {
      ...existingNodeData,
      ...currentNode,
      childNodeIds: childNodes.map(({ nodeId }) => nodeId),
    });

    for (const node of childNodes) {
      this.nodes.set(node.nodeId, { ...node, parentNodeId: currentNode.nodeId });
    }

    this.emitNodeChanges();
  }

  /**
   * Method used to rename a node.
   */
  public renameNode(nodeElement: GzNodeDirectorWebComponent<object>, newName: string) {
    const nodeId: string | number = nodeElement.nodeId;
    const node = this.nodes.get(nodeId);

    if (node) {
      node.nodeName = newName;
      this.emitNodeChanges();
    }
  }

  /**
   * Method used to delete a node.
   */
  public async deleteNode(nodeId: string | number) {
    const node = this.nodes.get(nodeId);

    if (node) {
      const parent = this.nodes.get(node.parentNodeId!);

      if (parent) {
        parent!.childNodeIds = parent?.childNodeIds?.filter(childNodeId => childNodeId !== nodeId);
      }

      this.nodes.delete(node.nodeId);
      this.emitNodeChanges();
    }
  }

  /**
   * Marks a node as being active.
   */
  public setNodeActive(node: NodeData, active: boolean = true): void {
    if (this.activeNodeId) {
      const activeNode = this.nodes.get(this.activeNodeId)!;
      this.nodes.set(this.activeNodeId, { ...activeNode, active: false });
    }

    node.parentNodeId ? (node.parentName = this.nodes.get(node.parentNodeId)?.nodeName) : (node.parentName = undefined);

    this.nodes.set(node.nodeId, { ...node, active });
    this.activeNodeId = node.nodeId;
    this.emitNodeChanges();
  }

  /**
   * Marks a node as being renamed.
   */
  public setEditState(nodeElement, editState: boolean = true): void {
    if (this.editNodeId) {
      const renameNode = this.nodes.get(this.editNodeId)!;
      this.nodes.set(this.editNodeId, { ...renameNode, editState: false });
    }

    nodeElement.node.parentNodeId
      ? (nodeElement.node.parentName = this.nodes.get(nodeElement.node.parentNodeId)?.nodeName)
      : (nodeElement.node.parentName = undefined);

    this.nodes.set(nodeElement.node.nodeId, { ...nodeElement.node, editState });
    this.editNodeId = nodeElement.node.nodeId;
    this.emitNodeChanges();
  }

  /**
   * Remove temporary nodes.
   */
  public deleteTemporaryNodes(nodeElement: GzNodeDirectorWebComponent<any>): void {
    nodeElement.nodes.forEach(node => {
      if (node.temporaryNode) {
        nodeElement.dataSource.deleteNode(node.nodeId);
      }
    });
  }

  /**
   * Opens up tree structure to the given node.
   * Marks parent node as being active.
   * Returns false if node doesn't exist on dataSource.
   */
  public navigateToContainer(nodeId: string): boolean {
    const node = this.nodes.get(nodeId);

    if (!node) {
      return false;
    }

    const parentNode = this.nodes.get(node.parentNodeId!)!;

    this.mapNodeParents(node, parentNode => (parentNode.isOpen = true));
    this.setNodeActive(parentNode);
    this.emitNodeChanges();

    return true;
  }

  private mapNodeParents(node: NodeData, cb: Function): void {
    if (node.parentNodeId) {
      const parentNode = this.nodes.get(node.parentNodeId);

      if (parentNode) {
        cb(parentNode);
        this.mapNodeParents(parentNode, cb);
      }
    }
  }

  /**
   * Navigate to the container node via static nodes, only missing static nodes need to be added.
   * Merges existing nodes with the ones passed in.
   */
  public navigateToNodeWithStaticNodes(staticNodes: Array<StaticNode>, endNodeId: string): void {
    this.loopTroughStaticNodesAndSetNodes(staticNodes);

    const endNode = this.nodes.get(endNodeId);

    if (endNode) {
      const endNodeParent = this.nodes.get(endNode.parentNodeId!)!;

      this.navigateToContainer(endNodeId);
      this.setNodeActive(endNodeParent, true);
    }
  }

  private loopTroughStaticNodesAndSetNodes(staticNodes: Array<StaticNode>, parentNodeId?: string | undefined): void {
    for (const staticNode of staticNodes) {
      const existingNode = this.nodes.get(staticNode.nodeId) || { parentNodeId: undefined };
      const childNodes = staticNode.childNodes || [];

      this.nodes.set(staticNode.nodeId, {
        ...existingNode,
        parentNodeId: parentNodeId || existingNode?.parentNodeId,
        ...staticNode,
        childNodeIds: childNodes.map(({ nodeId }) => nodeId),
      });

      if (childNodes.length) {
        this.loopTroughStaticNodesAndSetNodes(childNodes, staticNode.nodeId);
      }
    }
  }

  /**
   * Method that returns boolean if a node is part or not of a container node.
   */
  protected nodeIsChildNode(targetNode: NodeData, lookupNode: NodeData): boolean {
    for (const nodeId of lookupNode.childNodeIds || []) {
      if (nodeId === targetNode.nodeId) {
        return true;
      }

      const childNode = this.nodes.get(nodeId);

      if (childNode?.childNodeIds) {
        return this.nodeIsChildNode(targetNode, childNode);
      }
    }

    return false;
  }

  /**
   * Method used to trigger change detection in the UI
   */
  protected emitNodeChanges() {
    this.nodes$.next(cloneDeep(this.nodes));
  }
}
